import React, { useState, useEffect } from 'react';

const ProfileCompletionAnalysis = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/analytics/profile-completion`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        const result = await response.json();
        setData(result);
      }
    } catch (error) {
      console.error('Error fetching profile completion data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    
    const handleRefresh = () => {
      setLoading(true);
      fetchData();
    };
    
    window.addEventListener('analyticsRefresh', handleRefresh);
    return () => window.removeEventListener('analyticsRefresh', handleRefresh);
  }, []);

  if (loading) {
    return (
      <div className="space-y-6">
        {[...Array(2)].map((_, i) => (
          <div key={i} className="bg-white p-6 rounded-lg shadow animate-pulse">
            <div className="h-6 bg-gray-200 rounded mb-4 w-1/3"></div>
            <div className="h-64 bg-gray-200 rounded"></div>
          </div>
        ))}
      </div>
    );
  }

  const getCompletionRate = (completed, total) => {
    return total > 0 ? ((completed / total) * 100).toFixed(1) : 0;
  };

  const CompletionCard = ({ title, completed, total, color = 'blue' }) => {
    const percentage = getCompletionRate(completed, total);
    const colorClasses = {
      blue: 'bg-blue-50 text-blue-700 border-blue-200',
      green: 'bg-green-50 text-green-700 border-green-200',
      purple: 'bg-purple-50 text-purple-700 border-purple-200',
      orange: 'bg-orange-50 text-orange-700 border-orange-200'
    };

    return (
      <div className={`p-4 rounded-lg border-l-4 ${colorClasses[color]}`}>
        <h4 className="font-medium mb-2">{title}</h4>
        <div className="flex items-center justify-between mb-2">
          <span className="text-2xl font-bold">{completed}</span>
          <span className="text-sm">of {total}</span>
        </div>
        <div className="mb-2">
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className={`h-2 rounded-full ${color === 'blue' ? 'bg-blue-600' : color === 'green' ? 'bg-green-600' : color === 'purple' ? 'bg-purple-600' : 'bg-orange-600'}`}
              style={{ width: `${percentage}%` }}
            ></div>
          </div>
        </div>
        <p className="text-sm font-medium">{percentage}% completion rate</p>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Overall Completion Summary */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-semibold mb-4">Profile Completion Overview</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {data.map((roleData, index) => (
            <div key={index} className="text-center">
              <h4 className="font-medium text-gray-900 mb-4 capitalize">{roleData._id}s</h4>
              <div className="space-y-4">
                <CompletionCard 
                  title="Fully Completed"
                  completed={roleData.completed}
                  total={roleData.total}
                  color="green"
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Detailed Completion Analysis */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-semibold mb-4">Profile Section Completion</h3>
        <div className="space-y-6">
          {data.map((roleData, index) => (
            <div key={index}>
              <h4 className="font-medium text-gray-900 mb-4 capitalize text-lg">
                {roleData._id}s ({roleData.total} total users)
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <CompletionCard 
                  title="Education Info"
                  completed={roleData.withEducation}
                  total={roleData.total}
                  color="blue"
                />
                <CompletionCard 
                  title="Interests"
                  completed={roleData.withInterests}
                  total={roleData.total}
                  color="purple"
                />
                <CompletionCard 
                  title="Preferred Countries"
                  completed={roleData.withPreferredCountries}
                  total={roleData.total}
                  color="orange"
                />
                <CompletionCard 
                  title="Personal Info"
                  completed={roleData.withPersonalInfo}
                  total={roleData.total}
                  color="green"
                />
                <CompletionCard 
                  title="Test Scores"
                  completed={roleData.withTestScores}
                  total={roleData.total}
                  color="blue"
                />
                <div className="p-4 bg-gray-50 rounded-lg border-l-4 border-gray-300">
                  <h4 className="font-medium mb-2">Overall Completion</h4>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-2xl font-bold text-gray-700">{roleData.completed}</span>
                    <span className="text-sm">of {roleData.total}</span>
                  </div>
                  <div className="mb-2">
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-gray-600 h-2 rounded-full"
                        style={{ width: `${getCompletionRate(roleData.completed, roleData.total)}%` }}
                      ></div>
                    </div>
                  </div>
                  <p className="text-sm font-medium text-gray-700">
                    {getCompletionRate(roleData.completed, roleData.total)}% fully completed
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Completion Insights */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-semibold mb-4">Insights & Recommendations</h3>
        <div className="space-y-4">
          {data.map((roleData, index) => {
            const completionRate = getCompletionRate(roleData.completed, roleData.total);
            const educationRate = getCompletionRate(roleData.withEducation, roleData.total);
            const interestsRate = getCompletionRate(roleData.withInterests, roleData.total);
            const testScoresRate = getCompletionRate(roleData.withTestScores, roleData.total);

            return (
              <div key={index} className="p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-2 capitalize">{roleData._id}s Analysis</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-700">
                      <span className="font-medium">Completion Rate:</span> {completionRate}%
                      {completionRate < 50 && <span className="text-red-600 ml-1">(Low)</span>}
                      {completionRate >= 50 && completionRate < 80 && <span className="text-yellow-600 ml-1">(Moderate)</span>}
                      {completionRate >= 80 && <span className="text-green-600 ml-1">(High)</span>}
                    </p>
                    <p className="text-gray-700">
                      <span className="font-medium">Most Complete Section:</span> 
                      {Math.max(educationRate, interestsRate, testScoresRate) === educationRate && ' Education'}
                      {Math.max(educationRate, interestsRate, testScoresRate) === interestsRate && ' Interests'}
                      {Math.max(educationRate, interestsRate, testScoresRate) === testScoresRate && ' Test Scores'}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-700">
                      <span className="font-medium">Least Complete Section:</span>
                      {Math.min(educationRate, interestsRate, testScoresRate) === educationRate && ' Education'}
                      {Math.min(educationRate, interestsRate, testScoresRate) === interestsRate && ' Interests'}
                      {Math.min(educationRate, interestsRate, testScoresRate) === testScoresRate && ' Test Scores'}
                    </p>
                    <p className="text-gray-700">
                      <span className="font-medium">Incomplete Profiles:</span> {roleData.total - roleData.completed}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ProfileCompletionAnalysis;